import { useRef, useState } from 'react';
import { PRODUCT_NAME_DISPLAY } from '../../brand';
import { pushToast } from '../Toast';
import { getMasterVolume, setMasterVolume } from '../../game/audio';
import { ExitConfirmModal } from '../modals/ExitConfirmModal';

function closeApp() {
  // Telegram WebApp close when embedded; harmless no-op in browser/tests.
  (window as unknown as { Telegram?: { WebApp?: { close?: () => void } } })
    .Telegram?.WebApp?.close?.();
}

export function AppHeader() {
  const [muted, setMuted] = useState(false);
  const prevVol = useRef<number | null>(null);
  const [confirm, setConfirm] = useState(false);

  // Coarse global mute for the header. Uses the combined master level
  // (getMasterVolume = max of sfx/music); restoring sets both channels to that
  // level, so a custom sfx/music balance isn't preserved — acceptable for a
  // quick header toggle. Fine-grained mix lives in Settings.
  function toggleSound() {
    if (muted) {
      setMasterVolume(prevVol.current ?? getMasterVolume());
      setMuted(false);
    } else {
      prevVol.current = getMasterVolume();
      setMasterVolume(0);
      setMuted(true);
    }
  }

  return (
    <div className="cradle-app" style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: 50, zIndex: 200,
      // .cradle-app carries min-height:100% for the menu shell; on this fixed
      // 50px bar that rule inflated the header to full-viewport height and
      // covered the entire menu (the "stuck dark screen" on Telegram WebView).
      minHeight: 0,
      background: 'linear-gradient(180deg, #0a1224 0%, #07090f 100%)',
      boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px',
    }}>
      <div style={{ width: 32 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 18, height: 18, borderRadius: 5,
          background: 'radial-gradient(circle at 30% 25%, #fff8d2, #f5c542 45%, #7a4f08)',
          boxShadow: '0 0 6px rgba(245,197,66,0.4)',
        }} />
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 13, letterSpacing: '0.14em', color: '#fff' }}>
          {PRODUCT_NAME_DISPLAY}
        </div>
        <span className="beta-pulse" style={{
          fontFamily: 'var(--f-display)', fontSize: 8.5, letterSpacing: '0.14em', color: 'var(--cyan)',
          padding: '1.5px 5px', border: '0.5px solid rgba(61,213,255,0.5)', borderRadius: 3,
        }}>BETA</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button aria-label="notifications" onClick={() => pushToast({
          variant: 'info', title: 'НОВОЕ', body: 'Получай уведомления здесь',
        })} style={hdrBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 16h12l-2-3V9a4 4 0 10-8 0v4z"/>
            <path d="M10 19a2 2 0 004 0"/>
          </svg>
        </button>
        <button aria-label="sound" onClick={toggleSound} style={{ ...hdrBtn, color: muted ? 'var(--tx-faint)' : 'var(--gold-hi)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" fillOpacity="0.3"/>
            {muted ? (
              <path d="M16 9l5 6m0 -6l-5 6" strokeWidth="2"/>
            ) : (
              <>
                <path d="M16 9c1.5 1 1.5 5 0 6"/>
                <path d="M19 6c3 3 3 9 0 12"/>
              </>
            )}
          </svg>
        </button>
        <button aria-label="close" onClick={() => setConfirm(true)} style={hdrBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 3 L 11 11 M 11 3 L 3 11"/>
          </svg>
        </button>
      </div>

      <ExitConfirmModal
        open={confirm}
        onCancel={() => setConfirm(false)}
        onConfirm={() => { setConfirm(false); closeApp(); }}
      />
    </div>
  );
}

const hdrBtn: React.CSSProperties = {
  appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
  width: 30, height: 30, borderRadius: 999, display: 'grid', placeItems: 'center',
  color: 'var(--tx-dim)', fontSize: 15,
};
